import React, { useState } from "react";
import { Container, Reveal, SectionHeading } from "../components/common";
import { PageHero } from "../components/PageHero";
import { Marquee } from "../components/Marquee";
import { GALLERY, IMPACT_STATS, IMAGES, PROGRAMS } from "../lib/content";
import { fetchGallery, fetchImpactStats, fetchPrograms } from "../lib/api";
import { mapImpactStatRows, mapProgramRows } from "../lib/dynamicMappers";
import { useApiData } from "../hooks/useApiData";

const mapGalleryRows = (rows) =>
  rows.map((row) => ({
    src: row.image_path,
    alt: row.title,
    cat: row.caption || row.image_type,
    project: row.image_type,
  }));

export default function Gallery() {
  const gallery = useApiData(fetchGallery, GALLERY, mapGalleryRows);
  const stats = useApiData(fetchImpactStats, IMPACT_STATS, mapImpactStatRows);
  const programs = useApiData(fetchPrograms, PROGRAMS, mapProgramRows);
  const highlights = stats.slice(0, 4);
  const filters = [{ id: "all", name: "All Projects" }, ...programs.map((p) => ({ id: p.id, name: p.name }))];
  const [activeProject, setActiveProject] = useState("all");
  const filtered = activeProject === "all" ? gallery : gallery.filter((g) => g.project === activeProject);

  return (
    <>
      <PageHero
        overline="Impact & Gallery"
        title="Moments of change, captured on the ground"
        subtitle="Behind every number is a face, a smile, a life turning a corner. Here are glimpses from the field."
        image={IMAGES.health}
      />

      <section className="py-16 bg-white" data-testid="gallery-highlights">
        <Container>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {highlights.map((h, i) => (
              <Reveal key={h.label} delay={i * 0.06}>
                <div className="rounded-2xl bg-teal-50 p-6 text-center">
                  <div className="font-display text-3xl font-extrabold text-teal-700">
                    {h.value.toLocaleString("en-IN")}
                    {h.suffix}
                  </div>
                  <div className="mt-2 text-sm font-medium text-navy">{h.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <Marquee dark />

      <section className="py-20 lg:py-28 bg-offwhite" data-testid="gallery-grid-section">
        <Container>
          <SectionHeading
            overline="From the Field"
            title="A grassroots gallery"
            subtitle="Education camps, health drives, skill training and school support — captured across our regions."
            center
          />
          <div className="flex flex-wrap justify-center gap-2.5 mb-10" data-testid="gallery-filters">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setActiveProject(f.id)}
                data-testid={`gallery-filter-${f.id}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  activeProject === f.id ? "bg-teal-700 text-white" : "bg-white text-teal-700 border border-teal-100 hover:bg-teal-50"
                }`}
              >
                {f.name}
              </button>
            ))}
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {filtered.map((g, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <figure className="group relative mb-5 break-inside-avoid rounded-3xl overflow-hidden shadow-soft" data-testid={`gallery-item-${i}`}>
                  <img
                    src={g.src}
                    alt={g.alt}
                    className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                      i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-square" : "aspect-[4/5]"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <figcaption className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-coral">{g.cat}</span>
                    <div className="text-white font-display font-semibold">{g.alt}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-slate-400 font-mono">
            Placeholder imagery — real Janma Foundation photographs coming soon.
          </p>
        </Container>
      </section>
    </>
  );
}
